import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Button, Link } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { LoginStepHeading } from "./LoginStepHeading";
import { OrDivider } from "./OrDivider";
import { ResendCountdown } from "./ResendCountdown";

const OTP_CODE_LENGTH = 8;

/** Digits only, capped at the code length. */
function normalizeOtpCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, OTP_CODE_LENGTH);
}

// How often we reconcile the input's DOM value into React state. Must be well
// under the parent's 1s countdown tick so an autofilled code reaches state
// before a re-render can reassert the (stale) controlled value. See the sync
// effect below for why this is needed.
const OTP_AUTOFILL_SYNC_INTERVAL_MS = 150;

/** A single alternative sign-in action offered below the code entry. */
export interface OtpAlternativeAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
}

interface OtpEntryFormProps {
  email: string;
  loading: boolean;
  errorMessage?: string;
  /** Whether the resend cooldown is currently active. */
  isResendCoolingDown: boolean;
  /**
   * Absolute deadline (ms epoch) the resend cooldown counts down to. The seconds
   * tick in a leaf so they don't re-render this form; only used while
   * `isResendCoolingDown`.
   */
  resendCooldownDeadline?: number;
  /** Whether the current code can no longer be submitted. */
  isCodeExpired?: boolean;
  /** Whether the current code submit action should be blocked. */
  isSubmitBlocked?: boolean;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => void;
  onBack: () => void;
  /** When provided, renders a secondary alternative-method button above the back link. */
  alternativeAction?: OtpAlternativeAction;
  /** Enables the redesigned heading and action hierarchy without changing the legacy flow. */
  isRedesigned?: boolean;
}

/**
 * Code-entry screen shown after the user requests an email OTP. The user types
 * the 8-digit code from the email here, on the same browser that requested it.
 * The input holds the raw consecutive digits (no grouping space) so it validates
 * against `[0-9]*`, copies as consecutive digits, and lets OS one-time-code
 * autofill drop the code in cleanly. Wide letter-spacing keeps it readable.
 */
export function OtpEntryForm(props: OtpEntryFormProps): React.ReactElement {
  const { tu } = useTu("login");
  const {
    email,
    loading,
    errorMessage,
    isResendCoolingDown,
    resendCooldownDeadline,
    isCodeExpired = false,
    isSubmitBlocked = isCodeExpired,
    onSubmit,
    onResend,
    onBack,
    alternativeAction,
    isRedesigned = false,
  } = props;

  const [code, setCode] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isCodeComplete = code.length === OTP_CODE_LENGTH;
  const resendActionLabel = tu("Resend code");
  const legacyErrorResendIndex = errorMessage?.indexOf(resendActionLabel) ?? -1;
  const inlineResendLinkSx = {
    backgroundColor: "transparent",
    border: 0,
    color: "primary.main",
    cursor: loading ? "default" : "pointer",
    font: "inherit",
    padding: 0,
    verticalAlign: "baseline",
    "&:disabled": {
      color: "text.disabled",
      cursor: "default",
    },
  };

  useEffect(() => {
    if (!isSubmitBlocked || isResendCoolingDown || loading) {
      return;
    }

    formRef.current?.querySelector<HTMLButtonElement>("[data-resend-code-button]")?.focus();
  }, [errorMessage, isSubmitBlocked, isResendCoolingDown, loading]);

  // Adopt autofilled codes that never fired `onChange`. In the Android System
  // WebView, tapping the one-time-code autofill/keyboard suggestion sets the
  // input's value WITHOUT dispatching an input event React observes, so `code`
  // stays "". The parent re-renders every second (resend/validity countdown),
  // and React then reasserts the controlled `value={code}` = "", silently
  // wiping the autofilled digits. Polling the DOM value into state closes that
  // gap before the next re-render can wipe it. Typed/pasted input still flows
  // through `onChange`; this only catches the event-less autofill case.
  useEffect(() => {
    if (isSubmitBlocked) {
      return undefined;
    }
    const input = inputRef.current;
    if (!input) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      const domCode = normalizeOtpCode(input.value);
      setCode((current) => (current === domCode ? current : domCode));
    }, OTP_AUTOFILL_SYNC_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isSubmitBlocked]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!isCodeComplete || isSubmitBlocked) {
      return;
    }
    await onSubmit(code);
  }

  function renderResendLink(variant: "body1" | "body2") {
    return (
      <Link
        component="button"
        type="button"
        variant={variant}
        underline="hover"
        disabled={loading || isResendCoolingDown}
        data-resend-code-button
        sx={inlineResendLinkSx}
        onClick={onResend}
      >
        {resendActionLabel}
      </Link>
    );
  }

  return (
    <Box
      component="form"
      ref={formRef}
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {isRedesigned ? (
        <LoginStepHeading>{tu("Enter your code")}</LoginStepHeading>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
          <Text bold variant="h5" sx={{ color: "base.base" }}>
            {isSubmitBlocked ? tu("Get a new code") : tu("Enter your code")}
          </Text>
        </Box>
      )}
      <Text variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu("We sent an 8-digit code to {{email}}.", { values: { email } })}
        {!isSubmitBlocked && <> {tu("Enter it below to sign in.")}</>}
        {(!isRedesigned || !isSubmitBlocked || !isDefined(errorMessage) || isResendCoolingDown) && (
          <>
            {" "}
            {isResendCoolingDown && isDefined(resendCooldownDeadline) ? (
              <ResendCountdown deadline={resendCooldownDeadline}>
                {(seconds) =>
                  seconds === 1
                    ? tu("You can resend the code in 1 second.")
                    : tu("You can resend the code in {{seconds}} seconds.", { values: { seconds } })
                }
              </ResendCountdown>
            ) : (
              <>{renderResendLink("body1")}.</>
            )}
          </>
        )}
      </Text>
      {!isSubmitBlocked && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <Text semibold variant="body2" sx={{ color: "base.muted" }}>
            {tu("Verification code")}
          </Text>
          <TextInput
            fullWidth
            required
            inputRef={inputRef}
            // `type="tel"` (not "text") forces a numeric telephone keypad on
            // Android with no IME composition, so typed digits register against
            // the controlled value; "text" + inputMode="numeric" is ignored by
            // Samsung/Android keyboards, showing a composing QWERTY keyboard.
            type="tel"
            placeholder="00000000"
            value={code}
            disabled={loading}
            autoComplete="one-time-code"
            // Descriptive name/id give OS autofill heuristics an extra signal
            // (alongside autocomplete) that this field expects a one-time code.
            name="one-time-code"
            id="single-factor-otp"
            inputProps={{
              "aria-label": tu("Verification code"),
              inputMode: "numeric",
              pattern: "[0-9]*",
              // No maxLength: the onChange strip+slice is authoritative, so a
              // paste with leading/embedded whitespace can't be truncated to
              // fewer than 8 digits before we clean it.
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1.5rem",
                // Wider than default so the consecutive digits stay readable
                // without a grouping space in the value.
                letterSpacing: "0.5rem",
                textAlign: "center",
                backgroundColor: "background.standout",
              },
            }}
            onChange={(event) => {
              setCode(normalizeOtpCode(event.target.value));
            }}
          />
        </Box>
      )}
      {isDefined(errorMessage) && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {isRedesigned && isSubmitBlocked && !isResendCoolingDown ? (
            <>
              {errorMessage} {renderResendLink("body2")}.
            </>
          ) : legacyErrorResendIndex >= 0 ? (
            <>
              {errorMessage.slice(0, legacyErrorResendIndex)}
              {renderResendLink("body2")}
              {errorMessage.slice(legacyErrorResendIndex + resendActionLabel.length)}
            </>
          ) : (
            errorMessage
          )}
        </Text>
      )}
      {!isSubmitBlocked && (
        <LoadingButton
          fullWidth
          type="submit"
          variant="primary"
          size="large"
          isLoading={loading}
          disabled={!isCodeComplete}
        >
          {tu("Sign in")}
        </LoadingButton>
      )}
      {isDefined(alternativeAction) && (
        <>
          {isRedesigned && <OrDivider />}
          <LoadingButton
            fullWidth
            variant="secondary"
            size="large"
            isLoading={alternativeAction.isLoading ?? false}
            disabled={loading}
            sx={{ textTransform: "none" }}
            onClick={alternativeAction.onClick}
          >
            {alternativeAction.label}
          </LoadingButton>
        </>
      )}
      {/* Tertiary escape hatch stays last, directly above the footer. */}
      <Button
        variant="text"
        disabled={loading}
        sx={{ textTransform: "none", color: "primary.main" }}
        onClick={onBack}
      >
        {tu("Go back to login page")}
      </Button>
    </Box>
  );
}
